import {
  AI_IN_TICKET_DETAILS,
  NoData,
  SkeletonLoading,
  ClassicView,
  EventEmitter,
  AppContext,
  useProduct,
  COMMON_CONSTANTS,
  UnknownTicketActions,
  useSuperListHook,
  ALL_TABLES
} from '@nile/components';
import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback
} from 'react';
import { withRouter } from 'react-router-dom';
import { useQuery, NetworkStatus } from '@apollo/client';

import envConfig from '@nile/home/src/Config/env';
import { FormattedMessage } from 'react-intl';
import SizeMe from 'react-sizeme';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import TicketKanbanCard from '@nile/mangifera/src/Containers/CardContainers/TicketKanbanCard';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  GET_TICKET_LIST_CLASSIC,
  savePersonalization,
  getPersonalization
} from '@nile/graphql';
import { omit, cloneDeep, concat, isEmpty } from 'lodash';
import { sortActionsTicketKanbanMap } from '@nile/mangifera/src/Constants/Tickets';
import AdvancedTicketFilter from '@nile/mangifera/src/Components/Ticket/AdvancedTicketFilter';
import { DEFAULT_TICKET_CONDITION } from '@nile/components/src/Pebbles/AdvancedTicketFilter';
import { getTicketUpdates } from '@nile/mangifera/src/Utils/Hooks';
import { useGlobalState } from '@nile/components/src/Managers/StateManager';
import { MainContainer } from '@nile/mangifera/src/Containers/ChatContainers';
import * as _ from 'lodash';
import useClassicViewHook from '@nile/components/src/Hooks/useClassicViewHook';

// import { SVGIcon } from '@superops/super-icons';
// import { DisplayText, TextStyle, Button } from '@superops/ui-design-system';

const TicketDetailsLoading = () => {
  return (
    <SkeletonLoading
      hideStepNav
      hideTable
      showPlainCardView={false}
      showCardView={false}
      showConversationCard
      showPropertyCard
      showInsighteCard
      noThumbnail
      newLayout
    />
  );
};

const TicketClassicView = ({ ticketViews, match, size }) => {
  const { width } = size;
  interface Ticket {
    ticketId: string;
    displayId: string;
    subject: string;
    priority: null;
    createdTime: Date;
    firstResponseDueTime?: null;
    ticketType?: string;
    dormantType?: null;
    operationalStatus: 'ACTIVE' | 'DORMANT';
    status: 'Open';
    client?: {
      accountId: string;
      name: string;
    } | null;
    requester?: {
      firstName: string;
      lastName: string;
      userId: string;
      name: string;
    } | null;
    technician?: {
      firstName: string;
      lastName: string;
      userId: string;
      name: string;
    } | null;
    sla?: {
      name: string;
      id: string;
    } | null;
    source: 'Schedule';
    firstResponseDueTimeLeft: null;
    resolutionDueTimeLeft: null;
    resolutionDueTime: null;
    resolutionViolated: false;
    resolutionPauseTime: null;
    resolutionTime: null;
  }

  const ROW_COUNT = 20;
  const context = useContext(TicketContext);

  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  const user = {
    firstName: currentUser.firstName,
    userId: currentUser.userId,
    email: currentUser.email,
    lastName: currentUser.lastName
  };

  const DEFAULT_OPTIONS = {
    page: 1,
    sort: [
      {
        attribute: 'createdTime',
        order: 'DESC'
      }
    ],
    condition: null
  };

  const activeView = context.activeView ? context.activeView : null;
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);

  // Use the new classic view hook
  const {
    globalStateConditions,
    selectedItem,
    listData: hookListData,
    listInfo: hookListInfo,
    initializeGlobalConditions,
    updateGlobalStateConditions,
    updateSelectedItem,
    updateListData,
    updateListInfo,
    handleSearch,
    handleFilter,
    handleSort,
    resetFilters,
    showResetFilter,
    updateRecords,
    selectedRecords
  } = useClassicViewHook({
    module: 'ticket',
    viewId: match.params.id,
    defaultOptions: DEFAULT_OPTIONS
  });

  // Initialize global conditions on mount
  useEffect(() => {
    initializeGlobalConditions(activeView);
  }, [activeView, initializeGlobalConditions]);

  const ticketFetchListInfo = () => {
    const conditions =
      globalStateConditions || initializeGlobalConditions(activeView);
    return {
      view: {
        id: ticketViews.find((item) => item.id === match.params.id).id
      },
      page: conditions.pageNumber || 1,
      pageSize: ROW_COUNT,
      sort: conditions?.sort,
      condition: context.viewConditions ?? conditions.conditions
    };
  };

  // Use hook data as primary state, with local fallback
  const [ticketList, setTicketList] = useState(hookListData || []);
  const [listInfo, setListInfo] = useState(
    hookListInfo || ticketFetchListInfo()
  );

  const {
    data: ticketListData,
    error: ticketListError,
    loading: ticketListLoading,
    refetch: refetchTicketList,
    networkStatus
  } = useQuery(GET_TICKET_LIST_CLASSIC, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      listInfo: ticketFetchListInfo(),
      isMSPProduct
    },
    onCompleted: (res) => {
      const tickets = res?.getTicketList?.tickets || [];
      const listInfoData = res?.getTicketList?.listInfo || {};

      setTicketList(tickets);
      setListInfo(listInfoData);

      // Update hook store
      updateListData(tickets);
      updateListInfo(listInfoData);
    }
  });

  const isRefetching = networkStatus === NetworkStatus.refetch;

  const setViewConditionsCallback = (complexCondition) => {
    context.setViewConditions(complexCondition);
  };

  const updateClassicCard = useCallback(
    (updatedTicket) => {
      console.log(ticketList);
      setTicketList((prevList) => {
        const index = prevList?.findIndex(
          (ticket) => ticket.ticketId === updatedTicket.ticketId
        );
        if (index > -1) {
          const newList = [...prevList];
          newList[index] = updatedTicket;
          updateListData(newList);
          return newList;
        }
        const returnableList = [...prevList, updatedTicket];
        updateListData(returnableList);
        return returnableList;
      });
    },
    [ticketList, updateListData]
  );

  // Sync hook state with local state
  useEffect(() => {
    if (hookListData && hookListData !== ticketList) {
      setTicketList(hookListData);
    }
  }, [hookListData]);

  useEffect(() => {
    if (hookListInfo && hookListInfo !== listInfo) {
      setListInfo(hookListInfo);
    }
  }, [hookListInfo]);

  useEffect(() => {
    EventEmitter.subscribe('updateClassicCard', updateClassicCard);
    return function cleanup() {
      EventEmitter.unsubscribe('updateClassicCard');
    };
  }, [updateClassicCard]);

  return (
    <ClassicView
      listViews={ticketViews}
      searchTerms={['displayId', 'subject']}
      setViewConditionsCallback={setViewConditionsCallback}
      listData={ticketList || []}
      refetchList={refetchTicketList}
      listLoading={ticketListLoading || isRefetching}
      getPersonalizationCategory="TicketClassicViewPersonalization"
      monicaAiOptions={AI_IN_TICKET_DETAILS}
      itemKeyName="ticketId"
      listInfo={listInfo}
      globalStateConfigName="classicViewTicketId"
      detailsViewComponent={MainContainer}
      detailsViewLoading={TicketDetailsLoading}
      cardComponent={TicketKanbanCard}
      context={context}
      defaultFilterCondition={DEFAULT_TICKET_CONDITION}
      AdvancedFilterComponent={AdvancedTicketFilter}
      sortConstant={sortActionsTicketKanbanMap}
      noDataMessage="alert.noTicketsFound.heading"
      module="ticket"
      globalStateConditions={globalStateConditions}
      setGlobalStateConditions={updateGlobalStateConditions}
      detailsPageNoDataImage="svg/pinkgirl.svg"
      classicViewHook={{
        handleSearch,
        handleFilter,
        handleSort,
        resetFilters,
        showResetFilter,
        selectedItem,
        updateSelectedItem,
        updateRecords,
        selectedRecords
      }}
    />
  );
};

export default withRouter(SizeMe()(TicketClassicView));
